import { Trans } from 'react-i18next'
import { Link } from 'react-router-dom'
import clsx from 'clsx'

import { BrandKitsAPI, GetImageMetdataLink } from '@makyoapp/makyo-sdk'
import { OrgPermission } from '@makyoapp/makyo-sdk/models/ACL'
import { ImageMetadata } from '@makyoapp/makyo-sdk/models/Files'
import { IProjectBrief } from '@makyoapp/makyo-sdk/models/Projects'
import { Edit16, Lock24, Setting24, Trash16 } from '@makyoapp/makyo-icon/icon'
import { MoreDropdown, Tip, useTheme, useTip } from '@makyoapp/makyo-ui'

import { useOrgPerm } from '@/hooks/useOrgPerm'
import { BrandKit } from '@/shared/brand-kit'
import { LOGO_PLACEHOLDER } from '@/shared/constants'
import { BrandKitColorPreview } from '@/components/BrandKit/BrandKitColorPreview'
import { StackedAppTd } from '@/components/TdItem'

interface BrandKitCardProps {
  brandKit: BrandKit
  canDelete?: boolean
  setDel: (item: { name: string; id: string; error: string | null }) => void
  setRenameItem: (item: BrandKit) => void
  apps: IProjectBrief[]
}

export function BrandKitCard({ brandKit, setDel, canDelete, setRenameItem, apps = [] }: BrandKitCardProps) {
  const canEditBrandKit = useOrgPerm(OrgPermission.BrandKitEdit)

  const { state: themeState } = useTheme()

  const { id, name } = brandKit
  const lockTip = <Trans>This is the only brand kit, it can&apos;t be deleted</Trans>

  const { x, y, strategy, refs, context, placement, isOpen, arrowRef, getReferenceProps, getFloatingProps } = useTip()

  // delete
  const handleDeleteBrandKit = (id: string) => {
    BrandKitsAPI.ReqDelete(id, true)
      .then(() => {
        setDel({ name, id, error: null })
      })
      .catch((error) => {
        setDel({ name, id, error: error.message })
      })
  }

  return (
    <div className=''>
      <div
        className={clsx(
          'mk-bk-card ',
          'flex flex-col justify-between border bdr rounded-sm overflow-hidden',
          'bg-white dark:bg-slate-800'
        )}
      >
        <div className='mk-bk-title flex-c justify-between h-[62px] px-4 py-3 rounded-t border-b border-zinc-200 dark:border-slate-600'>
          <Link to={id} className='w-full txt text-sm font-medium'>
            {name}
          </Link>

          <StackedAppTd
            items={apps.map((app) => ({
              id: app._id,
              title: app._Name,
              icon: app._AppIcon,
              iconBgColor: app._IconBackgroundColor,
            }))}
            className='animate-show'
          />

          <div className='flex-c'>
            {!canDelete && (
              <>
                <button ref={refs.setReference} className='text-light relative' {...getReferenceProps()}>
                  <Lock24 width={16} height={16} />
                </button>
                {isOpen && (
                  <Tip
                    x={x}
                    y={y}
                    placement={placement}
                    strategy={strategy}
                    ref={refs.setFloating}
                    {...getFloatingProps()}
                    context={context as any}
                    arrowRef={arrowRef}
                  >
                    {lockTip}
                  </Tip>
                )}
              </>
            )}
            {canEditBrandKit && (
              <MoreDropdown placement='bottom-end'>
                <Link to={id} className='mk-mdd-item'>
                  <Setting24 width={16} height={16} />
                  <div>
                    <Trans>Edit</Trans>
                  </div>
                </Link>

                <MoreDropdown.MenuItem onClick={() => setRenameItem(brandKit)}>
                  <Edit16 />
                  <div>
                    <Trans>Rename</Trans>
                  </div>
                </MoreDropdown.MenuItem>

                {canDelete && (
                  <MoreDropdown.MenuItem
                    className='mk-mdd-item red'
                    onClick={() => {
                      handleDeleteBrandKit(id)
                    }}
                  >
                    <Trash16 />
                    <div>
                      <Trans>Delete</Trans>
                    </div>
                  </MoreDropdown.MenuItem>
                )}
              </MoreDropdown>
            )}
          </div>
        </div>

        <Link
          to={id}
          className='mk-bk-logo-wrap flex items-center justify-center border-b border-zinc-200 dark:border-slate-600 p-4'
        >
          <div className='mk-bk-logo flex items-center justify-center w-[100px] h-[100px]'>
            <img
              alt=''
              className={clsx('max-w-full max-h-full object-contain rounded-lg')}
              src={brandKit.logos?.[0] ? GetImageMetdataLink(brandKit.logos[0], ImageMetadata.XS) : LOGO_PLACEHOLDER}
            />
          </div>
        </Link>

        <BrandKitColorPreview brandKit={brandKit} mode={themeState.mode} />
      </div>
    </div>
  )
}
